import React, { useContext, useState } from 'react';
import { useRequestsContext } from '@/common/hooks/requestHook';
import {
	addMessageToTicket,
	updateTicketStatus,
	uploadFileToQuestionTicket,
} from '@/API/repositories/tickets';
import {
	CommunicationWrapper,
	SendButton,
	AttachIcon,
	FileInput,
	FileInputWrapper,
	FileInputNameWrapper,
	SplitLine,
	SpinnerWrapper,
	RichTextEditorWrapper,
} from './Communication.styled';
import { QUESTION_TICKET_STATUS_OPTIONS } from '@/common/constants/questionTicket';
import { CUSTOM_SELECT_THEME } from '@/common/constants/customSelectTheme';
import SelectInput from '@/common/components/SelectInput';
import { Colors } from '@/common/colors/colors';
import Icon from '@/common/components/Icon';
import { getMessagePlaceHolder } from '@/common/functions/getMessagePlaceHolder';
import { handleExtractDelta } from '@/common/functions/handleExtractDelta';
import RichTextEditor from '@/common/components/richTextEditor/RichTextEditor';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import { AuthContext } from '@/common/contexts/authContext';


const Communication = ({
	loadData,
	selectedTicket,
	quillRef,
	fileRef,
	handleFileChanges,
	isFileSelected,
	setMessageLoading,
	messageLoading,
}) => {
	const { authUser } = useContext(AuthContext);

	const [statusUpdatingTicket, setStatusUpdatingTicket] = useState();

	const { makeRequest } = useRequestsContext();
	const { addMessage } = useMessageQueueContext();

	const sendMessage = async (e) => {
		e && e.preventDefault();

		if (fileRef.current?.files[0]) {
			return await sendFile();
		}

		if (quillRef.current.getText()?.trim()) {
			await sendTextMessage();
		}
	};

	const sendTextMessage = async (e) => {
		e && e.preventDefault();

		if (messageLoading === selectedTicket) {
			return addMessage('Wait untill previous message will be sended', 'error');
		}

		setMessageLoading(() => selectedTicket);

		const payload = {};
		payload.quill_delta = handleExtractDelta(quillRef);
		quillRef.current?.setContents?.(null);

		const response = await makeRequest(
			addMessageToTicket.bind(null, selectedTicket._id, payload),
		);

		if (response.data) {
			await loadData();
		}

		setMessageLoading(() => null);
	};

	const sendFile = async () => {
		if (messageLoading === selectedTicket) {
			return addMessage('Wait untill previous message will be sended', 'error');
		}
		setMessageLoading(() => selectedTicket);

		const formData = new FormData();

		formData.append('file', fileRef.current?.files[0]);

		fileRef.current.value = '';
		handleFileChanges();

		const response = await makeRequest(
			uploadFileToQuestionTicket.bind(null, formData, selectedTicket._id),
		);

		if (response.data) {
			await loadData();
		}

		setMessageLoading(() => null);
	};

	const isDisableCommunication =
		!selectedTicket ||
		(selectedTicket.type === 'document' &&
			['accepted_pending', 'declined_pending', 'declined', 'accepted'].includes(
				selectedTicket.status,
			)) ||
		selectedTicket.status === 'solved' ||
		(selectedTicket.status === 'new' &&
			selectedTicket.created_by !== authUser.id);

	const changeTicketStatus = async (status) => {
		setStatusUpdatingTicket(() => selectedTicket);

		await sendMessage();

		const payload = {};

		payload.status = status.value;

		const response = await makeRequest(
			updateTicketStatus.bind(null, selectedTicket?._id, payload),
		);

		if (response?.data) {
			await loadData();
		}

		setStatusUpdatingTicket(() => false);
	};

	const isStatusDisabled =
		!selectedTicket ||
		statusUpdatingTicket ||
		selectedTicket?.status === 'solved' ||
		(selectedTicket?.status !== 'pending' &&
			selectedTicket?.created_by === authUser.id);

	return (
		<CommunicationWrapper onSubmit={(e) => sendMessage(e)}>
			<SplitLine />
			<AttachIcon
				htmlFor='ticket_file'
			>
				<Icon name={'exclamation'} />
			</AttachIcon>
			<FileInputWrapper display={!isFileSelected ? 'none' : 'block'}>
				<FileInputNameWrapper>
					<label htmlFor='ticket_file'>
						<Icon name={'file'} />
					</label>
					<FileInput
						onChange={() => handleFileChanges()}
						hidden={!isFileSelected ? 'hidden' : false}
						type='file'
						accept='application/pdf,image/jpg,image/png,image/jpeg'
						id='ticket_file'
						disabled={isDisableCommunication}
						ref={fileRef}
					/>
				</FileInputNameWrapper>
			</FileInputWrapper>
			<RichTextEditorWrapper>
				{!isFileSelected && (
					<RichTextEditor
						ref={quillRef}
						readOnly={isDisableCommunication}
						placeholder={getMessagePlaceHolder(
							selectedTicket,
							isDisableCommunication,
						)}
					/>
				)}
			</RichTextEditorWrapper>
			<SendButton
				disabled={isDisableCommunication}
				type='submit'
			>
				<Icon name='paper-plane'/>
			</SendButton>			
			{selectedTicket?.type === 'question' &&
				(statusUpdatingTicket ? (
					<SpinnerWrapper>
						<Icon size={28} name='spinner' spin/>
					</SpinnerWrapper>
				) : (
					<SelectInput
						bgColor={isStatusDisabled ? 'transparent' : Colors.purple}
						showLabel={false}
						selectWidth={150}
						options={QUESTION_TICKET_STATUS_OPTIONS}
						setSelected={(value) => changeTicketStatus(value)}
						disabled={isStatusDisabled}
						selected={QUESTION_TICKET_STATUS_OPTIONS.find(
							(o) => o.value === selectedTicket?.status,
						)}
						customThemeColors={CUSTOM_SELECT_THEME}
						color='#fff'
						rotation='rotateX(180deg) '
						reversed
						showSquare={true}
						textColor={isStatusDisabled ? Colors.newDarkGray : '#fff'}
						placeholderColor='#fff'
					/>
				))}
		</CommunicationWrapper>
	);
};

export default Communication;
